import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash.isempty';
import { mediaBelow, pxToRem } from '../../assets/styles/helper';
import themeColors from '../../assets/styles/themeColors';
import { variables } from '../../assets/styles/variables';
import {
  AlertsCountLabel,
  EmptyState,
  EmptyStateIcon,
  PrimaryText,
  SecondaryText,
  UnstyledButton,
  List as BaseList,
} from './Common';
import { formatDate as formatShortDate } from './Common/dateHelpers';

const List = styled(BaseList)`
  margin: 0 0 ${pxToRem(16)} 0;
  width: 100%;
`;

const Row = styled.li`
  padding: ${pxToRem(12)} 0;
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};

  &:last-child {
    border-bottom: none;
  }
`;

const PriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const AlertPrice = styled.p`
  font-family: ${variables.family.goldSilver};
  font-weight: 600;
  font-style: normal;
  font-size: 16px;
  line-height: 140%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: ${themeColors.colorTextPrimary};
  margin: 0 0 2px 0;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const AlertSetDate = styled.span`
  display: block;
  margin-bottom: 2px;
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: ${themeColors.colorTextSecondary};
`;

const AlertExpiryDate = styled.span`
  display: block;
  font-family: ${variables.family.goldSilver};
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};
`;

const AlertsCountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${pxToRem(12)};

  ${AlertsCountLabel} {
    margin: 0;
  }
`;

const ClearAllButton = styled(UnstyledButton)`
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 140%;
  letter-spacing: 0%;
  text-align: right;
  vertical-align: middle;
  color: ${({ $readOnly }) =>
    $readOnly ? themeColors.colorDisabledIcon : themeColors.colorTextSecondary};
`;

const MobileBreak = styled.br`
  display: none;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    display: block;
  }
`;

const SetAgainButton = styled(UnstyledButton)`
  flex-shrink: 0;
  font-family: ${variables.family.goldSilver};
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 140%;
  text-align: right;
  color: ${({ $readOnly }) =>
    $readOnly ? themeColors.colorDisabledIcon : '#505575'};
`;

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return dateStr;
  const month = date.toLocaleDateString(undefined, { month: 'short' });
  const day = String(date.getDate()).padStart(2, '0');
  return `${month} ${day}, ${date.getFullYear()}`;
};

export const ExpiredAlertsHeader = ({
  count,
  onClearAll,
  isReadOnly,
  onBlockedAction,
}) => {
  const { t } = useTranslation();
  return (
    <AlertsCountRow>
      <AlertsCountLabel>
        {count > 1
          ? t('gtt.labels.alertsExpired_other', { count })
          : t('gtt.labels.alertsExpired_one', { count })}
      </AlertsCountLabel>
      <ClearAllButton
        type="button"
        $readOnly={isReadOnly}
        data-cy="button-clear-all-expired-alerts"
        onClick={isReadOnly ? onBlockedAction : onClearAll}
      >
        {t('gtt.labels.clearAll')}
      </ClearAllButton>
    </AlertsCountRow>
  );
};

ExpiredAlertsHeader.propTypes = {
  count: PropTypes.number,
  onClearAll: PropTypes.func,
  isReadOnly: PropTypes.bool,
  onBlockedAction: PropTypes.func,
};

const ExpiredAlerts = ({
  alertsList,
  onSetAgain,
  isReadOnly,
  onBlockedAction,
}) => {
  const { t } = useTranslation();

  if (!alertsList || isEmpty(alertsList)) {
    const noExpiredAlertsSub = t('gtt.labels.noExpiredAlertsSub');
    const breakIndex = noExpiredAlertsSub.indexOf('without') + 'without'.length;
    const firstLine = noExpiredAlertsSub.slice(0, breakIndex);
    const secondLine = noExpiredAlertsSub.slice(breakIndex).trim();

    return (
      <EmptyState>
        <EmptyStateIcon />
        <PrimaryText>{t('gtt.labels.noExpiredAlerts')}</PrimaryText>
        <SecondaryText>
          {firstLine} <MobileBreak />
          {secondLine}
        </SecondaryText>
      </EmptyState>
    );
  }

  return (
    <List>
      {alertsList.map((alert, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <Row key={index}>
          <PriceRow>
            <AlertPrice>{alert.alertPrice}</AlertPrice>
            <SetAgainButton
              type="button"
              $readOnly={isReadOnly}
              data-cy={`button-set-again-alert-${index}`}
              onClick={() =>
                isReadOnly ? onBlockedAction?.() : onSetAgain?.(alert)
              }
            >
              {t('gtt.labels.setAgain')}
            </SetAgainButton>
          </PriceRow>
          <AlertSetDate>
            {t('gtt.labels.alertSetOn', {
              date: formatShortDate(alert.alertDate),
              method: alert.alertMethod,
            })}
          </AlertSetDate>
          <AlertExpiryDate>
            {t('gtt.labels.alertExpiredOn', {
              date: formatDate(alert.alertExpiryDate),
            })}
            {alert?.alertTriggeredDate
              ? t('gtt.labels.alertTriggeredOn', {
                  date: formatDate(alert.alertTriggeredDate),
                })
              : t('gtt.labels.notTriggered')}
          </AlertExpiryDate>
        </Row>
      ))}
    </List>
  );
};

ExpiredAlerts.propTypes = {
  alertsList: PropTypes.arrayOf(PropTypes.shape({})),
  onSetAgain: PropTypes.func,
  isReadOnly: PropTypes.bool,
  onBlockedAction: PropTypes.func,
};

export default ExpiredAlerts;
